import React, { useEffect, useMemo } from 'react';
import clsx from 'clsx';
import type { AddressSubValue } from '@checkout-page/schemas';
import { getAddressRule, type SubFieldKey } from '../../lib/addressRules';
import { useCheckoutLocalization } from '../../hooks/useCheckoutLocalization';
import { translateAddressKey, translateAddressLabel } from '../../lib/addressTranslations';
import { TextInput, Select } from '../Form';
import StyledElement from '../StyledElement';
import CountrySelect, {
  type LimitAllowedCountries,
} from '../CheckoutForm/components/CountrySelect';
import { AutocompleteInput } from './AutocompleteInput';
import { AddressPhoneInput } from './AddressPhoneInput';

const inputClass = clsx('cp_form-textinput');

const selectClass = clsx('cp_form-select');

// Compact rows: keep the standard cp_form-textinput / cp_form-select classes so
// typography, padding, theme colors and chevron styling match the rest of the
// form, but force border / radius off so each row is flush inside the outer
// rounded container. Focus ring/shadow comes from the standard
// cp_form-textinput:focus rule (overridden in CheckoutCSS for grouped layout).
const rowInputClass = clsx(inputClass, '!border-0 !rounded-none');

const rowSelectClass = clsx(selectClass, '!border-0 !rounded-none');

// Country-specific example placeholders for fields where the format is non-obvious.
// Falls back to the label for other fields.
const EXAMPLE_PLACEHOLDERS: Record<string, Partial<Record<SubFieldKey, string>>> = {
  US: { postalCode: '12345', line1: '123 Main St' },
  CA: { postalCode: 'A1A 1A1', line1: '123 Main St' },
  GB: { postalCode: 'SW1A 1AA', line1: '123 High Street' },
  AU: { postalCode: '2000', line1: '123 Example St' },
  DE: { postalCode: '10115', line1: 'Musterstraße 1' },
  FR: { postalCode: '75001', line1: "1 rue de l'Exemple" },
  NL: { postalCode: '1011 AB', line1: 'Voorbeeldstraat 1' },
  BE: { postalCode: '1000', line1: 'Voorbeeldstraat 1' },
  ES: { postalCode: '28001', line1: 'Calle Ejemplo 1' },
  IT: { postalCode: '00100', line1: 'Via Esempio 1' },
  IE: { postalCode: 'D02 X285' },
  SE: { postalCode: '111 22' },
  NO: { postalCode: '0150' },
  DK: { postalCode: '1050' },
  FI: { postalCode: '00100' },
  CH: { postalCode: '8001' },
  AT: { postalCode: '1010' },
  PL: { postalCode: '00-001' },
  JP: { postalCode: '100-0001' },
  BR: { postalCode: '01000-000' },
  MX: { postalCode: '01000' },
  IN: { postalCode: '110001' },
  SG: { postalCode: '238859' },
  NZ: { postalCode: '6011' },
  ZA: { postalCode: '8001' },
};

const placeholderFor = (country: string, field: SubFieldKey, fallbackLabel: string): string => {
  const example = EXAMPLE_PLACEHOLDERS[country]?.[field];
  return example ?? fallbackLabel;
};

type Props = {
  value: AddressSubValue;
  onChange: (value: AddressSubValue) => void;
  onCountryChange?: (country: string) => void;
  required?: boolean;
  errors?: Partial<Record<SubFieldKey | 'country' | 'name' | 'phone', string>>;
  enableAutocomplete?: boolean;
  limitAllowedCountries?: LimitAllowedCountries;
  idPrefix?: string;
  showName?: boolean;
  nameLabel?: string;
  showPhone?: boolean;
  phoneLabel?: string;
};

export function AddressInputs({
  value,
  onChange,
  onCountryChange,
  required = true,
  errors,
  enableAutocomplete,
  limitAllowedCountries,
  idPrefix = 'addr',
  showName = false,
  nameLabel,
  showPhone = false,
  phoneLabel,
}: Props) {
  const rule = useMemo(() => getAddressRule(value.country || 'US'), [value.country]);

  const { locale } = useCheckoutLocalization();
  const t = (label: string) => translateAddressLabel(locale, label);
  const nameLabelText = nameLabel ?? translateAddressKey(locale, 'ui.fullName');
  const phoneLabelText = phoneLabel ?? translateAddressKey(locale, 'ui.phone');

  const restrictedToSingle =
    limitAllowedCountries?.enabled === true &&
    Array.isArray(limitAllowedCountries.countries) &&
    limitAllowedCountries.countries.length === 1
      ? limitAllowedCountries.countries[0]
      : null;

  useEffect(() => {
    if (restrictedToSingle && value.country !== restrictedToSingle) {
      onChange({ ...value, country: restrictedToSingle, region: undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restrictedToSingle, value.country]);

  const setField = (key: keyof AddressSubValue, v: string) => {
    onChange({ ...value, [key]: v });
  };

  const countryId = `${idPrefix}-country`;
  const nameId = `${idPrefix}-name`;
  const phoneId = `${idPrefix}-phone`;

  // Build the rendered row list so we can mark the first and last row for
  // CSS targeting (focus-ring corner-rounding to match the container shape).
  const rowKeys: string[] = [];
  if (showName) rowKeys.push('name');
  if (showPhone) rowKeys.push('phone');
  if (!restrictedToSingle) rowKeys.push('country');
  for (const o of rule.order) rowKeys.push(o.field);
  const firstRowKey = rowKeys[0];
  const lastRowKey = rowKeys[rowKeys.length - 1];
  const positionFor = (key: string): 'first' | 'last' | undefined =>
    key === firstRowKey ? 'first' : key === lastRowKey ? 'last' : undefined;

  return (
    <div className="cp-address-inputs">
      <div className="cp-address-inputs-group border-[color:var(--cp_checkout-form-fields-input-border-color)] bg-[var(--cp_checkout-form-fields-input-background-color)]">
        {showName && (
          <div className="cp-address-row" data-row-position={positionFor('name')}>
            <StyledElement
              elementPath="checkout.form.fields.input"
              as={TextInput}
              id={nameId}
              name={nameId}
              aria-label={nameLabelText}
              type="text"
              placeholder={nameLabelText}
              required={required}
              value={value.name ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setField('name', e.target.value)
              }
              className={rowInputClass}
            />
            {errors?.name && (
              <div role="alert" className="text-sm text-red-600 px-3 pb-2">
                {errors.name}
              </div>
            )}
          </div>
        )}

        {showPhone && (
          <AddressPhoneInput
            id={phoneId}
            value={value.phone ?? ''}
            onChange={v => setField('phone', v)}
            country={value.country}
            required={required}
            placeholder={phoneLabelText}
            error={errors?.phone}
            rowPosition={positionFor('phone')}
          />
        )}

        {!restrictedToSingle && (
          <div className="cp-address-row" data-row-position={positionFor('country')}>
            <CountrySelect
              id={countryId}
              ariaLabel={translateAddressKey(locale, 'ui.country')}
              value={value.country}
              onChange={code => {
                onCountryChange?.(code);
                onChange({ ...value, country: code, region: undefined });
              }}
              required={required}
              limitAllowedCountries={limitAllowedCountries}
              className={rowSelectClass}
            />
            {errors?.country && (
              <div role="alert" className="text-sm text-red-600 px-3 pb-2">
                {errors.country}
              </div>
            )}
          </div>
        )}

        {rule.order.map(o => {
          const id = `${idPrefix}-${o.field}`;
          const err = errors?.[o.field];
          const requiredHere = required && rule.required.includes(o.field);
          const labelText = t(rule.labels[o.field]);
          const placeholder = placeholderFor(value.country || 'US', o.field, labelText);

          if (o.field === 'region' && rule.regions) {
            return (
              <div
                key={o.field}
                className="cp-address-row"
                data-row-position={positionFor(o.field)}
              >
                <StyledElement
                  elementPath="checkout.form.fields.input"
                  as={Select}
                  id={id}
                  name={id}
                  aria-label={labelText}
                  required={requiredHere}
                  value={value.region ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setField('region', e.target.value)
                  }
                  className={rowSelectClass}
                >
                  <option value="" disabled={requiredHere}>
                    {labelText}
                  </option>
                  {rule.regions.map(r => (
                    <option key={r.code} value={r.code} className="cp_form-option">
                      {r.name}
                    </option>
                  ))}
                </StyledElement>
                {err && (
                  <div role="alert" className="text-sm text-red-600 px-3 pb-2">
                    {err}
                  </div>
                )}
              </div>
            );
          }

          if (o.field === 'line1' && enableAutocomplete) {
            return (
              <div key="line1" className="cp-address-row" data-row-position={positionFor(o.field)}>
                <AutocompleteInput
                  id={id}
                  value={value.line1 ?? ''}
                  onChange={v => setField('line1', v)}
                  onPickAddress={partial => {
                    if (partial.country && partial.country !== value.country) {
                      onCountryChange?.(partial.country);
                    }
                    onChange({ ...value, ...partial });
                  }}
                  country={value.country}
                  placeholder={placeholder}
                  required={requiredHere}
                  className={rowInputClass}
                  hideLabel
                />
                {err && (
                  <div role="alert" className="text-sm text-red-600 px-3 pb-2">
                    {err}
                  </div>
                )}
              </div>
            );
          }

          return (
            <div key={o.field} className="cp-address-row" data-row-position={positionFor(o.field)}>
              <StyledElement
                elementPath="checkout.form.fields.input"
                as={TextInput}
                id={id}
                name={id}
                aria-label={labelText}
                type="text"
                placeholder={placeholder}
                required={requiredHere}
                value={(value[o.field] as string) ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setField(o.field, e.target.value)
                }
                className={rowInputClass}
              />
              {err && (
                <div role="alert" className="text-sm text-red-600 px-3 pb-2">
                  {err}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
